import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, TrendingUp, Users, MessageSquare, CheckCircle2, Loader2, ChevronDown, ChevronUp, Printer, Zap, Sparkles } from 'lucide-react';
import Button from '../../components/ui/Button';
import Card, { CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { useAnalyzeAllQuestions } from '../../hooks/useWorkflowAnalysis';
import { api, standaloneCompaniesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { StrategicQuestionAnalysisCard } from '../../components/analysis';
import { formatCurrencyValue, regionFromCountry } from '../../components/financial/utils/formatters';

/**
 * Round a percentage value to 2 decimals and strip trailing zeros so
 * "25" stays "25%" but "26.78887727162121" becomes "26.79%".
 * Renders "—" for null / undefined / NaN.
 */
function fmtPct(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'number' ? value : parseFloat(value as string);
  if (!Number.isFinite(num)) return '—';
  return `${Math.round(num * 100) / 100}%`;
}

interface Step1Props {
  caseId: string;
  wizardData: any;
  onNext: () => void;
  onBack: () => void;
}

export const Step1DiscoverySummary: React.FC<Step1Props> = ({ caseId, wizardData, onNext, onBack }) => {
  const { companyData, industryData, competitorsData, questionsData } = wizardData || {};

  const selectedCompetitors = competitorsData?.filter((c: any) => c.selected) || [];
  const selectedQuestions = questionsData?.filter((q: any) => q.selected) || [];

  // Hook for analyzing all questions
  const analyzeAllMutation = useAnalyzeAllQuestions();

  // State for analysis results and progress tracking
  const [analysisResults, setAnalysisResults] = useState<Record<string, any>>({});
  const [loadingResults, setLoadingResults] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [batchResult, setBatchResult] = useState<any>(null);
  const [hasExistingResults, setHasExistingResults] = useState(false);
  const [reanalyzingQuestionId, setReanalyzingQuestionId] = useState<string | null>(null);
  const [analyzingQuestionId, setAnalyzingQuestionId] = useState<string | null>(null);

  // Backend questions from Neo4j (with real sq_xxx IDs)
  const [backendQuestions, setBackendQuestions] = useState<any[]>([]);
  const [loadingBackendQuestions, setLoadingBackendQuestions] = useState(false);

  // Track if we've completed checking for existing results
  const [hasCheckedExisting, setHasCheckedExisting] = useState(false);

  // Collapsible sections state - collapsed by default
  const [companyExpanded, setCompanyExpanded] = useState(false);
  const [industryExpanded, setIndustryExpanded] = useState(false);
  const [competitorsExpanded, setCompetitorsExpanded] = useState(false);

  // Auto-trigger setup
  const [searchParams] = useSearchParams();
  const shouldAutoAnalyze = searchParams.get('autoAnalyze') === 'true';
  const hasTriggeredRef = useRef(false);
  const hasFetchedExistingRef = useRef(false);
  const hasFetchedBackendQuestionsRef = useRef(false);

  /**
   * Fetch strategic questions from Neo4j backend.
   * These have the real questionId values (sq_xxx) that the backend expects.
   */
  const fetchBackendQuestions = async () => {
    console.log('📋 Fetching strategic questions from Neo4j...');
    setLoadingBackendQuestions(true);

    try {
      const questions = await api.get(`/case-analysis/cases/${caseId}/questions`);

      if (questions && Array.isArray(questions) && questions.length > 0) {
        // Order by questionId so the positional "1, 2, 3…" badges track
        // sq_001, sq_002, … The endpoint returns graph order, which made the
        // list's first entry display as "1" while actually being sq_010.
        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
        setBackendQuestions(
          [...questions].sort((a: any, b: any) =>
            collator.compare(String(a?.questionId ?? ''), String(b?.questionId ?? '')),
          ),
        );
        console.log(`✅ Loaded ${questions.length} strategic questions from Neo4j:`,
          questions.map((q: any) => q.questionId));
      } else {
        console.warn('⚠️ No strategic questions found in Neo4j for this case');
      }
    } catch (error) {
      console.error('❌ Failed to fetch strategic questions from Neo4j:', error);
    } finally {
      setLoadingBackendQuestions(false);
    }
  };

  // Fetch backend questions on mount
  useEffect(() => {
    if (!hasFetchedBackendQuestionsRef.current && caseId) {
      hasFetchedBackendQuestionsRef.current = true;
      fetchBackendQuestions();
    }
  }, [caseId]);

  /**
   * Fetch existing analysis results from Neo4j.
   * Called after backendQuestions are loaded to check for existing analyses,
   * and after analysis completes to refresh from database.
   *
   * Uses backendQuestions (with real sq_xxx IDs) instead of wizard's selectedQuestions.
   */
  const fetchExistingAnalysisResults = async () => {
    if (backendQuestions.length === 0) {
      console.log('⏳ Waiting for backend questions to be loaded...');
      return;
    }

    console.log('📥 Fetching existing analysis results from Neo4j...');
    setLoadingResults(true);

    try {
      const results: Record<string, any> = {};
      let foundCount = 0;

      // Fetch analysis for each backend question (using real sq_xxx IDs)
      await Promise.all(
        backendQuestions.map(async (question: any) => {
          const questionId = question.questionId;
          if (!questionId) return;

          try {
            const analysisData = await standaloneCompaniesAPI.getQuestionAnalysis(
              caseId,
              questionId
            );

            // Check if analysis exists (has_analysis or has_workflow_result)
            if (analysisData && (analysisData.has_analysis || analysisData.has_workflow_result)) {
              results[questionId] = analysisData;
              foundCount++;
            }
          } catch (error) {
            // 404 means no analysis yet - this is expected for unanalyzed questions
            console.debug(`No existing analysis for question ${questionId}`);
          }
        })
      );

      if (foundCount > 0) {
        setAnalysisResults(results);
        setHasExistingResults(true);
        setAnalysisComplete(true);
        console.log(`✅ Loaded ${foundCount} existing analysis results from Neo4j`);
      } else {
        console.log('ℹ️ No existing analysis results found in Neo4j');
      }
    } catch (error) {
      console.error('Failed to fetch existing analysis results:', error);
    } finally {
      setLoadingResults(false);
      setHasCheckedExisting(true);  // Mark that we've completed the check
    }
  };

  // Fetch existing results after backend questions are loaded
  useEffect(() => {
    if (!hasFetchedExistingRef.current && backendQuestions.length > 0 && caseId) {
      hasFetchedExistingRef.current = true;
      fetchExistingAnalysisResults();
    }
  }, [caseId, backendQuestions.length]);

  // Process analysis results from mutation response
  // Note: The batch analysis endpoint already returns all results,
  // so we don't need to make separate API calls to fetch them
  const processAnalysisResults = (mutationResults: any[]) => {
    setLoadingResults(true);
    try {
      const results: Record<string, any> = {};

      // Map each result by question_id for lookup
      mutationResults.forEach((result: any) => {
        const questionId = result.question_id;
        if (questionId) {
          results[questionId] = result;
        }
      });

      setAnalysisResults(results);
      console.log('Processed analysis results:', Object.keys(results).length, 'results');
    } catch (error) {
      console.error('Failed to process analysis results:', error);
    } finally {
      setLoadingResults(false);
    }
  };

  // Handler for analyzing questions
  const handleAnalyzeQuestions = async () => {
    console.log('🚀 Starting analysis for case:', caseId);
    setIsAnalyzing(true);
    setAnalysisComplete(false);

    try {
      console.log('📤 Calling analyzeAllMutation.mutateAsync...');
      const result = await analyzeAllMutation.mutateAsync({
        caseId,
        // Use backend default (ollama with fallback to openai/anthropic)
        // If you want to force a specific provider, use: llmProvider: 'ollama' | 'openai' | 'anthropic'
      });

      console.log('✅ Analysis mutation completed. Result:', result);

      // Store the batch result
      setBatchResult(result);

      // Check if result has the expected structure
      if (result && typeof result === 'object' && 'total_questions' in result) {
        toast.success(
          `Analysis completed! ${result.total_questions} questions analyzed (${result.completed} succeeded, ${result.failed} failed).`
        );
      } else {
        toast.success('Analysis completed successfully!');
        console.warn('⚠️ Unexpected result structure:', result);
      }

      // Process the analysis results directly from the mutation response (immediate feedback)
      console.log('📥 Processing analysis results from mutation...');
      if (result.results && Array.isArray(result.results)) {
        processAnalysisResults(result.results);
        console.log('✅ Analysis results processed successfully');
      } else {
        console.warn('⚠️ No results array in mutation response');
      }

      setAnalysisComplete(true);

      // Also fetch from Neo4j to ensure we have the persisted data
      // This validates storage and provides consistent data structure
      console.log('🔄 Refreshing results from Neo4j...');
      await fetchExistingAnalysisResults();
      console.log('✅ Neo4j results refreshed');
    } catch (error: unknown) {
      console.error('❌ Analysis failed:', error);
      const err = error as { message?: string; response?: unknown; stack?: string };
      console.error('Error details:', {
        message: err?.message,
        response: err?.response,
        stack: err?.stack
      });
      toast.error('Failed to start analysis. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handler for re-analyzing a single question
  const handleReanalyzeQuestion = async (questionId: string) => {
    console.log('🔄 Re-analyzing question:', questionId);
    setReanalyzingQuestionId(questionId);

    try {
      // Call the analyze endpoint with force_regenerate=true
      const result = await api.post(
        `/case-analysis/cases/${caseId}/questions/${questionId}/analyze?force_regenerate=true`
      );

      console.log('✅ Re-analysis complete for question:', questionId, result);

      // Update the analysis results with the new result
      setAnalysisResults(prev => ({
        ...prev,
        [questionId]: result
      }));

      toast.success('Question re-analyzed successfully!');

      // Optionally refresh from Neo4j to ensure consistency
      await fetchExistingAnalysisResults();

    } catch (error) {
      console.error('❌ Re-analysis failed:', error);
      toast.error('Failed to re-analyze question. Please try again.');
    } finally {
      setReanalyzingQuestionId(null);
    }
  };

  // Handler for analyzing a single question (first time analysis)
  const handleAnalyzeQuestion = async (questionId: string) => {
    console.log('🔬 Analyzing question:', questionId);
    setAnalyzingQuestionId(questionId);

    try {
      // Call the analyze endpoint (without force_regenerate for first-time analysis)
      const result = await api.post(
        `/case-analysis/cases/${caseId}/questions/${questionId}/analyze`
      );

      console.log('✅ Analysis complete for question:', questionId, result);

      // Update the analysis results with the new result
      setAnalysisResults(prev => ({
        ...prev,
        [questionId]: result
      }));

      toast.success('Question analyzed successfully!');

      // Refresh from Neo4j to ensure consistency
      await fetchExistingAnalysisResults();

    } catch (error) {
      console.error('❌ Analysis failed:', error);
      toast.error('Failed to analyze question. Please try again.');
    } finally {
      setAnalyzingQuestionId(null);
    }
  };

  // Handler for printing strategic questions and analysis
  const handlePrintAnalysis = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print the analysis');
      return;
    }

    // Collect mermaid diagrams for rendering
    const mermaidDiagrams: { id: string; code: string }[] = [];
    backendQuestions.forEach((question: any, index: number) => {
      const analysis = analysisResults[question.questionId];
      const consulting = analysis?.consulting_analysis;
      if (consulting?.mermaid_diagram && consulting?.show_flow_visualization) {
        mermaidDiagrams.push({
          id: `mermaid-${index}`,
          code: consulting.mermaid_diagram
        });
      }
    });

    // Generate printable HTML content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Strategic Questions Analysis - ${companyData?.name || 'Company'}</title>
        <script src="https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js"></script>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 900px;
            margin: 0 auto;
            padding: 40px 20px;
          }
          h1 { color: #1a1a1a; border-bottom: 2px solid #6366f1; padding-bottom: 10px; }
          h2 { color: #374151; margin-top: 30px; }
          h3 { color: #4b5563; margin-top: 20px; }
          .question-card {
            background: #f9fafb;
            border-left: 4px solid #6366f1;
            padding: 20px;
            margin: 20px 0;
            page-break-inside: avoid;
          }
          .question-text { font-size: 18px; font-weight: 600; color: #1f2937; }
          .badge {
            display: inline-block;
            padding: 2px 10px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
            margin-left: 10px;
          }
          .badge-critical { background: #fef2f2; color: #991b1b; }
          .badge-high { background: #fffbeb; color: #92400e; }
          .badge-medium { background: #eff6ff; color: #1d4ed8; }
          .section { margin: 15px 0; }
          .section-title { font-weight: 600; color: #374151; margin-bottom: 8px; }
          .section-content { color: #4b5563; }
          .hypothesis {
            background: #f3e8ff;
            padding: 12px;
            margin: 8px 0;
            border-radius: 6px;
            border-left: 3px solid #a855f7;
          }
          .data-source {
            display: inline-block;
            background: #e0f2fe;
            padding: 4px 10px;
            margin: 4px;
            border-radius: 4px;
            font-size: 13px;
          }
          .phase {
            background: #f0fdf4;
            padding: 15px;
            margin: 10px 0;
            border-radius: 6px;
            border-left: 3px solid #22c55e;
          }
          .phase-title { font-weight: 600; color: #166534; }
          .deliverable {
            background: #fefce8;
            padding: 10px;
            margin: 6px 0;
            border-radius: 4px;
            border-left: 3px solid #eab308;
          }
          .meta { font-size: 12px; color: #9ca3af; margin-top: 10px; }
          .mermaid-container {
            background: #fafafa;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 20px;
            margin: 15px 0;
            text-align: center;
          }
          .mermaid-title {
            font-weight: 600;
            color: #6366f1;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .mermaid {
            display: flex;
            justify-content: center;
          }
          @media print {
            body { padding: 20px; }
            .question-card { break-inside: avoid; }
            .mermaid-container { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>Strategic Questions Analysis</h1>
        <p><strong>Company:</strong> ${companyData?.name || 'N/A'}</p>
        <p><strong>Industry:</strong> ${industryData?.name || 'N/A'}</p>
        <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>

        <h2>Strategic Questions (${Object.keys(analysisResults).length} Analyzed)</h2>

        ${backendQuestions.map((question: any, index: number) => {
          const analysis = analysisResults[question.questionId];
          const consulting = analysis?.consulting_analysis;

          return `
            <div class="question-card">
              <div class="question-text">
                ${index + 1}. ${question.text || question.question}
                <span class="badge badge-${(question.priority || '').toLowerCase()}">${question.priority || 'Medium'}</span>
              </div>

              ${consulting ? `
                <div class="section">
                  <div class="section-title">Analysis Category</div>
                  <div class="section-content">${consulting.question_category?.replace(/_/g, ' ').toUpperCase() || 'N/A'}</div>
                </div>

                <div class="section">
                  <div class="section-title">Executive Summary</div>
                  <div class="section-content">${consulting.executive_summary || 'N/A'}</div>
                </div>

                <div class="section">
                  <div class="section-title">Key Business Question</div>
                  <div class="section-content">${consulting.key_business_question || 'N/A'}</div>
                </div>

                ${consulting.mermaid_diagram && consulting.show_flow_visualization ? `
                  <div class="mermaid-container">
                    <div class="mermaid-title">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" stroke-width="2">
                        <circle cx="12" cy="5" r="3"/>
                        <line x1="12" y1="8" x2="12" y2="14"/>
                        <circle cx="6" cy="19" r="3"/>
                        <circle cx="18" cy="19" r="3"/>
                        <line x1="12" y1="14" x2="6" y2="16"/>
                        <line x1="12" y1="14" x2="18" y2="16"/>
                      </svg>
                      Analysis Flow Diagram
                    </div>
                    <pre class="mermaid">${consulting.mermaid_diagram}</pre>
                  </div>
                ` : ''}

                ${consulting.hypotheses && consulting.hypotheses.length > 0 ? `
                  <div class="section">
                    <div class="section-title">Hypotheses to Test</div>
                    ${consulting.hypotheses.map((h: any) => `
                      <div class="hypothesis">
                        <strong>${h.statement}</strong>
                        <div style="font-size: 13px; color: #6b7280; margin-top: 5px;">${h.rationale}</div>
                      </div>
                    `).join('')}
                  </div>
                ` : ''}

                ${consulting.data_sources ? `
                  <div class="section">
                    <div class="section-title">Required Data Sources</div>
                    ${(consulting.data_sources.available || []).map((ds: any) => `
                      <span class="data-source">✓ ${ds.name}</span>
                    `).join('')}
                    ${(consulting.data_sources.gaps || []).map((ds: any) => `
                      <span class="data-source" style="background: #fee2e2;">⚠ ${ds.name} (Gap)</span>
                    `).join('')}
                  </div>
                ` : ''}

                ${consulting.recommended_approach && consulting.recommended_approach.length > 0 ? `
                  <div class="section">
                    <div class="section-title">Recommended Approach</div>
                    ${consulting.recommended_approach.map((phase: any) => `
                      <div class="phase">
                        <div class="phase-title">Phase ${phase.phase_number}: ${phase.title}</div>
                        <div style="margin-top: 5px;">${phase.objective}</div>
                      </div>
                    `).join('')}
                  </div>
                ` : ''}

                ${consulting.expected_deliverables && consulting.expected_deliverables.length > 0 ? `
                  <div class="section">
                    <div class="section-title">Expected Deliverables</div>
                    ${consulting.expected_deliverables.map((d: any) => `
                      <div class="deliverable">
                        <strong>${d.title}</strong> (${d.type})
                        <div style="font-size: 13px; color: #6b7280;">${d.description}</div>
                      </div>
                    `).join('')}
                  </div>
                ` : ''}

                ${consulting.key_success_factors && consulting.key_success_factors.length > 0 ? `
                  <div class="section">
                    <div class="section-title">Key Success Factors</div>
                    <ul>${consulting.key_success_factors.map((f: string) => `<li>${f}</li>`).join('')}</ul>
                  </div>
                ` : ''}

                ${consulting.potential_risks && consulting.potential_risks.length > 0 ? `
                  <div class="section">
                    <div class="section-title">Potential Risks</div>
                    <ul>${consulting.potential_risks.map((r: string) => `<li>${r}</li>`).join('')}</ul>
                  </div>
                ` : ''}
              ` : `
                <div class="section">
                  <div class="section-content" style="color: #9ca3af;">Analysis not yet available</div>
                </div>
              `}

              <div class="meta">Question ID: ${question.questionId}</div>
            </div>
          `;
        }).join('')}

        <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #9ca3af;">
          Generated by Amplinth Strategic Analysis Platform
        </div>

        <script>
          // Initialize Mermaid with beautiful styling
          mermaid.initialize({
            startOnLoad: true,
            theme: 'base',
            securityLevel: 'loose',
            flowchart: {
              useMaxWidth: true,
              htmlLabels: true,
              curve: 'basis',
              padding: 15,
              nodeSpacing: 40,
              rankSpacing: 50
            },
            themeVariables: {
              primaryColor: '#818cf8',
              primaryTextColor: '#1e1b4b',
              primaryBorderColor: '#6366f1',
              secondaryColor: '#fcd34d',
              secondaryTextColor: '#78350f',
              secondaryBorderColor: '#f59e0b',
              tertiaryColor: '#6ee7b7',
              tertiaryTextColor: '#064e3b',
              tertiaryBorderColor: '#10b981',
              lineColor: '#6366f1',
              textColor: '#374151',
              mainBkg: '#f8fafc',
              nodeBorder: '#6366f1',
              fontFamily: 'system-ui, sans-serif',
              fontSize: '13px'
            }
          });
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();

    // Auto-trigger print dialog after Mermaid renders
    printWindow.onload = () => {
      // Wait for Mermaid to finish rendering diagrams
      setTimeout(() => {
        printWindow.print();
      }, 1500); // Give Mermaid time to render
    };
  };

  // AUTO-TRIGGER: Analyze on mount if autoAnalyze=true and no existing results
  // Uses backendQuestions (with real sq_xxx IDs) for analysis
  // IMPORTANT: Wait for hasCheckedExisting to ensure we don't re-analyze existing data
  useEffect(() => {
    const shouldTrigger =
      shouldAutoAnalyze &&
      !hasTriggeredRef.current &&
      backendQuestions.length > 0 &&  // Use backend questions (real IDs)
      !loadingBackendQuestions &&     // Wait for backend questions to load
      hasCheckedExisting &&           // CRITICAL: Wait for existing results check to complete
      !analysisComplete &&
      !isAnalyzing &&
      !loadingResults &&
      !hasExistingResults;            // Don't re-analyze if results already exist

    console.log('🔍 Auto-trigger check:', {
      shouldAutoAnalyze,
      hasTriggered: hasTriggeredRef.current,
      backendQuestionsCount: backendQuestions.length,
      loadingBackendQuestions,
      hasCheckedExisting,
      analysisComplete,
      isAnalyzing,
      loadingResults,
      hasExistingResults,
      shouldTrigger
    });

    if (shouldTrigger) {
      console.log('✨ Auto-triggering analysis for', backendQuestions.length, 'strategic questions (from Neo4j)');
      hasTriggeredRef.current = true;
      handleAnalyzeQuestions();
    }
  }, [shouldAutoAnalyze, backendQuestions.length, loadingBackendQuestions, hasCheckedExisting, analysisComplete, isAnalyzing, loadingResults, hasExistingResults]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">
          Discovery Phase Summary
        </h2>
        <p className="text-neutral-600">
          Review the key findings from the Discovery phase before proceeding to Analysis
        </p>
      </div>

      {/* Company Information - Collapsible */}
      {companyData && (
        <Card>
          <CardHeader
            className="cursor-pointer hover:bg-neutral-50 transition-colors"
            onClick={() => setCompanyExpanded(!companyExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-neutral-900">Company Information</h3>
                <span className="text-sm text-neutral-500">({companyData.name})</span>
              </div>
              {companyExpanded ? (
                <ChevronUp className="w-5 h-5 text-neutral-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-neutral-500" />
              )}
            </div>
          </CardHeader>
          {companyExpanded && (
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-1">Company Name</p>
                  <p className="text-base text-neutral-900">{companyData.name}</p>
                </div>

                {companyData.ticker && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700 mb-1">Ticker Symbol</p>
                    <p className="text-base text-neutral-900">{companyData.ticker}</p>
                  </div>
                )}

                {companyData.industry && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700 mb-1">Industry</p>
                    <p className="text-base text-neutral-900">{companyData.industry}</p>
                  </div>
                )}

                {companyData.description && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700 mb-1">Description</p>
                    <p className="text-base text-neutral-600">{companyData.description}</p>
                  </div>
                )}

                {companyData.financialData && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                    {companyData.financialData.revenue && (
                      <div>
                        <p className="text-sm font-medium text-neutral-700 mb-1">Revenue</p>
                        <p className="text-base text-neutral-900">{companyData.financialData.revenue}</p>
                      </div>
                    )}
                    {companyData.financialData.revenueGrowth && (
                      <div>
                        <p className="text-sm font-medium text-neutral-700 mb-1">Growth</p>
                        <p className="text-base text-neutral-900">{companyData.financialData.revenueGrowth}</p>
                      </div>
                    )}
                    {companyData.financialData.grossMargin && (
                      <div>
                        <p className="text-sm font-medium text-neutral-700 mb-1">Gross Margin</p>
                        <p className="text-base text-neutral-900">{companyData.financialData.grossMargin}</p>
                      </div>
                    )}
                    {companyData.financialData.operatingMargin && (
                      <div>
                        <p className="text-sm font-medium text-neutral-700 mb-1">Operating Margin</p>
                        <p className="text-base text-neutral-900">{companyData.financialData.operatingMargin}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardBody>
          )}
        </Card>
      )}

      {/* Industry Research - Collapsible */}
      {industryData && (
        <Card>
          <CardHeader
            className="cursor-pointer hover:bg-neutral-50 transition-colors"
            onClick={() => setIndustryExpanded(!industryExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-neutral-900">Industry Research</h3>
                <span className="text-sm text-neutral-500">({industryData.name})</span>
              </div>
              {industryExpanded ? (
                <ChevronUp className="w-5 h-5 text-neutral-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-neutral-500" />
              )}
            </div>
          </CardHeader>
          {industryExpanded && (
          <CardBody>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-1">Industry</p>
                <p className="text-base text-neutral-900">{industryData.name}</p>
              </div>

              {industryData.classification && (
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-1">Classification</p>
                  <p className="text-base text-neutral-900">{industryData.classification}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {industryData.marketSizeUSD && (
                  <div>
                    {/* Country-aware Market Size. When the wizard adapter
                        resolved a country-specific value (marketScope ===
                        'country'), render with the case's currency and label
                        it accordingly. Otherwise show the global TAM in USD. */}
                    {(() => {
                      const scope = (industryData as any)?.marketScope;
                      const isCountrySpecific = scope === 'country';
                      const country = companyData?.country;
                      const region = isCountrySpecific ? regionFromCountry(country) : undefined;
                      const size = industryData.marketSizeUSD ?? 0;
                      const label = isCountrySpecific
                        ? `Market Size (${country ?? 'Region'})`
                        : 'Market Size (Global)';
                      const formatted = region
                        ? formatCurrencyValue(size, region, /* compact */ true)
                        : `$${(size / 1e12).toFixed(2)}T`;
                      return (
                        <>
                          <p className="text-sm font-medium text-neutral-700 mb-1">{label}</p>
                          <p className="text-base text-neutral-900">{formatted}</p>
                        </>
                      );
                    })()}
                  </div>
                )}
                {industryData.marketGrowthRate && (
                  <div>
                    <p className="text-sm font-medium text-neutral-700 mb-1">Growth Rate</p>
                    <p className="text-base text-neutral-900">{fmtPct(industryData.marketGrowthRate)}</p>
                  </div>
                )}
              </div>

              {industryData.benchmarks && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-neutral-700 mb-3">Industry Benchmarks</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {industryData.benchmarks.gross_margin != null && (
                      <div>
                        <p className="text-xs text-neutral-600">Gross Margin</p>
                        <p className="text-sm font-medium text-neutral-900">{fmtPct(industryData.benchmarks.gross_margin)}</p>
                      </div>
                    )}
                    {industryData.benchmarks.operating_margin != null && (
                      <div>
                        <p className="text-xs text-neutral-600">Operating Margin</p>
                        <p className="text-sm font-medium text-neutral-900">{fmtPct(industryData.benchmarks.operating_margin)}</p>
                      </div>
                    )}
                    {industryData.benchmarks.roe != null && (
                      <div>
                        <p className="text-xs text-neutral-600">ROE</p>
                        <p className="text-sm font-medium text-neutral-900">{fmtPct(industryData.benchmarks.roe)}</p>
                      </div>
                    )}
                    {industryData.benchmarks.revenue_growth != null && (
                      <div>
                        <p className="text-xs text-neutral-600">Revenue Growth</p>
                        <p className="text-sm font-medium text-neutral-900">{fmtPct(industryData.benchmarks.revenue_growth)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardBody>
          )}
        </Card>
      )}

      {/* Competitors - Collapsible */}
      {selectedCompetitors.length > 0 && (
        <Card>
          <CardHeader
            className="cursor-pointer hover:bg-neutral-50 transition-colors"
            onClick={() => setCompetitorsExpanded(!competitorsExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" />
                <h3 className="text-lg font-semibold text-neutral-900">Top Competitors</h3>
                <Badge variant="secondary">{selectedCompetitors.length} selected</Badge>
              </div>
              {competitorsExpanded ? (
                <ChevronUp className="w-5 h-5 text-neutral-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-neutral-500" />
              )}
            </div>
          </CardHeader>
          {competitorsExpanded && (
          <CardBody>
            <div className="space-y-4">
              {selectedCompetitors.map((competitor: any, index: number) => (
                <div key={competitor.competitorId || index} className="p-4 bg-neutral-50 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-neutral-900">{competitor.name}</h4>
                    {competitor.market_share && (
                      <Badge variant="info">{competitor.market_share}% market share</Badge>
                    )}
                  </div>

                  {competitor.description && (
                    <p className="text-sm text-neutral-600 mb-3">{competitor.description}</p>
                  )}

                  {competitor.competitive_advantages && competitor.competitive_advantages.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-neutral-700 mb-2">Competitive Advantages:</p>
                      <div className="flex flex-wrap gap-2">
                        {competitor.competitive_advantages.map((advantage: string, i: number) => (
                          <span key={i} className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                            {advantage}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardBody>
          )}
        </Card>
      )}

      {/* Strategic Questions with Analysis Results - Uses backendQuestions with real sq_xxx IDs */}
      {(backendQuestions.length > 0 || loadingBackendQuestions) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-semibold text-neutral-900">Strategic Questions</h3>
                <Badge variant="secondary">{backendQuestions.length} questions</Badge>
                {Object.keys(analysisResults).length > 0 && (
                  <Badge variant="success">
                    {Object.keys(analysisResults).length} analyzed
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3">
                {(loadingResults || loadingBackendQuestions) && (
                  <div className="flex items-center gap-2 text-sm text-neutral-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{loadingBackendQuestions ? 'Loading questions...' : 'Loading results...'}</span>
                  </div>
                )}
                {Object.keys(analysisResults).length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrintAnalysis}
                    className="flex items-center gap-1.5"
                  >
                    <Printer className="w-4 h-4" />
                    Print Analysis
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              {backendQuestions.map((question: any, index: number) => {
                const analysisResult = analysisResults[question.questionId];

                // If we have analysis results, use the enhanced card
                if (analysisResult) {
                  return (
                    <StrategicQuestionAnalysisCard
                      key={question.questionId || index}
                      result={analysisResult}
                      expanded={index === 0} // Expand first by default
                      onReanalyze={handleReanalyzeQuestion}
                      isReanalyzing={reanalyzingQuestionId === question.questionId}
                    />
                  );
                }

                // Otherwise, show the basic question card (using backend question format)
                const isAnalyzingThis = analyzingQuestionId === question.questionId;
                return (
                  <div key={question.questionId || index} className="p-4 border-l-4 border-green-500 bg-green-50 rounded-r-lg">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-medium text-neutral-900 flex-1">{question.text || question.question}</p>
                      <div className="flex items-center gap-2 ml-2">
                        {question.priority && (
                          <Badge
                            variant={
                              question.priority === 'Critical' ? 'error' :
                              question.priority === 'High' ? 'warning' : 'info'
                            }
                          >
                            {question.priority}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {question.category && (
                      <p className="text-sm text-neutral-600">
                        <span className="font-medium">Category:</span> {question.category}
                      </p>
                    )}

                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xs text-neutral-400">
                        ID: {question.questionId}
                      </p>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleAnalyzeQuestion(question.questionId)}
                        disabled={isAnalyzingThis || isAnalyzing}
                        className="flex items-center gap-1.5"
                      >
                        {isAnalyzingThis ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4" />
                            Analyze
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Analyze Questions Section - Uses backendQuestions for real question IDs */}
      {backendQuestions.length > 0 && !analysisComplete && (
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-neutral-900">
              {isAnalyzing ? 'Analyzing Strategic Questions' : 'Ready to Analyze'}
            </h3>
          </CardHeader>
          <CardBody>
            {!isAnalyzing ? (
              <div className="space-y-4">
                <p className="text-neutral-600">
                  Found {backendQuestions.length} strategic question
                  {backendQuestions.length !== 1 ? 's' : ''} in Neo4j ready for analysis.
                </p>
                <p className="text-sm text-neutral-500">
                  This will classify each question and execute the appropriate AI workflow
                  to generate insights and recommendations. Analysis typically takes 2-10 minutes.
                </p>
                <Button
                  variant="primary"
                  onClick={handleAnalyzeQuestions}
                  disabled={loadingBackendQuestions}
                  className="w-full sm:w-auto"
                >
                  Analyze Strategic Questions
                </Button>
              </div>
            ) : (
              // Flow-wise progress skeleton — replaces the previous single-line
              // spinner. The backend's /analyze-all returns a final batch
              // result only when *all* questions are processed (no per-question
              // streaming yet), so we present the same N rows as the user is
              // about to analyse, each in a pulsing "queued" state. The intent
              // is honesty: we can't show real per-question progress without
              // backend support, but we *can* set expectations about scope,
              // duration, and the fact that the user is free to wait elsewhere.
              <div className="space-y-4" aria-busy="true" aria-live="polite">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-neutral-700">
                    Running AI workflow across{' '}
                    <span className="font-semibold">{backendQuestions.length}</span>{' '}
                    strategic question{backendQuestions.length !== 1 ? 's' : ''}.
                  </p>
                  <span className="text-xs text-neutral-500">~2–10 min total</span>
                </div>
                {/* Indeterminate bar — communicates "active" without
                    pretending to show a real percentage. Uses Tailwind's
                    built-in animate-pulse so we don't need a new keyframe
                    in tailwind.config.js. */}
                <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden">
                  <div className="h-full w-1/3 bg-primary-500 rounded-full animate-pulse" />
                </div>
                <div className="space-y-2">
                  {backendQuestions.map((q: any, idx: number) => (
                    <div
                      key={q.questionId || q.id || idx}
                      className="flex items-start gap-3 p-3 rounded-lg border border-neutral-200 bg-neutral-50"
                    >
                      <div className="w-7 h-7 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0 text-xs font-medium text-neutral-500">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <p className="text-sm text-neutral-800 line-clamp-2">
                          {q.questionText || q.text || `Question ${idx + 1}`}
                        </p>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-32 bg-neutral-200 rounded-full overflow-hidden">
                            <div className="h-full w-1/2 bg-primary-300 rounded-full animate-pulse" />
                          </div>
                          <span className="text-[11px] text-neutral-500">Queued</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <Sparkles className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-blue-900 leading-relaxed">
                    Feel free to switch to another tab — we'll keep working in the background.
                    The page will update automatically when each workflow finishes.
                  </p>
                </div>
              </div>
            )}
            {analysisComplete && batchResult && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-800">
                  <CheckCircle2 className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Analysis Complete!</p>
                    <p className="text-sm">
                      {batchResult.completed} of {batchResult.total_questions} questions analyzed successfully
                    </p>
                    {batchResult.failed > 0 && (
                      <p className="text-sm text-orange-600 mt-1">
                        {batchResult.failed} question(s) failed
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Summary Stats */}
      <Card className="bg-gradient-to-r from-green-50 to-blue-50">
        <CardBody>
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
            <h3 className="text-lg font-semibold text-neutral-900">Discovery Phase Complete</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-2xl font-bold text-neutral-900">{companyData ? '1' : '0'}</p>
              <p className="text-sm text-neutral-600">Company Analyzed</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">{industryData ? '1' : '0'}</p>
              <p className="text-sm text-neutral-600">Industry Researched</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">{selectedCompetitors.length}</p>
              <p className="text-sm text-neutral-600">Competitors Identified</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-neutral-900">{backendQuestions.length}</p>
              <p className="text-sm text-neutral-600">Strategic Questions</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onNext}>
          Continue to Enterprise Systems
        </Button>
      </div>
    </div>
  );
};

export default Step1DiscoverySummary;
